import ManagerRepository from 'coreClasses/ManagerRepository';
import Manager from 'coreClasses/Manager';
import {receivePromotions, selectPromotion, cleanupPromotions} from 'coreStore/data/promotions';
import PromotionModel from './PromotionModel';

const MAX_PROMOTIONS = 24;

/**
 * Represents PromotionsMgr
 */
class PromotionsMgr extends Manager {
	private loadingPromotionIds: Array<string>;

	/**
	 * Represents PromotionsMgr
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 * @protected
	 */
	constructor(managerRepository: ManagerRepository) {
		super(managerRepository);
		this.loadingPromotionIds = [];
	}

	/**
	 * Gets multiple promotions from store or API using getPromotions api call
	 * @param {Array<String>} promotionIds Array of promotion ids
	 * @param {Boolean} forceFetch Executes API call even if promotions are already stored
	 * @returns {Array<PromotionModel>} Array of promotions
	 */
	getPromotions(promotionIds: Array<string>, forceFetch: boolean = false) {
		const promotionIdsToReceive = promotionIds.filter((promotionId) => {
			return promotionId &&
				(forceFetch ||
					(!this.getStoredPromotion(promotionId) && !this.loadingPromotionIds.includes(promotionId))
				);
		});

		if (promotionIdsToReceive.length) {
			for (let i = 0; i < promotionIdsToReceive.length; i += MAX_PROMOTIONS) {
				this.fetchPromotions(promotionIdsToReceive.slice(i, i + MAX_PROMOTIONS));
			}
		}

		return promotionIds.map((promotionId) => {
			return new PromotionModel(this.getStoredPromotion(promotionId));
		});
	};

	/**
	 * Removes all stored promotions from redux store
	 */
	cleanupPromotions() {
		const store = this.managerRepository.getStoreMgr().getStore();

		store.dispatch(cleanupPromotions());
	};

	/**
	 * Executes API call to get multiple promotion
	 * Since limit for ids parameter in promotion API is 256 characters
	 * we divide the promotions to fetch into multiple calls if it's needed
	 * @param {Array<string>} promotionIds Array of promotion ids
	 */
	private async fetchPromotions(promotionIds: Array<string>) {
		this.loadingPromotionIds = this.loadingPromotionIds.concat(promotionIds);

		const promotionIdsStrings: string[] = [];
		let promotions = null;

		if (promotionIds.join(',').length <= 256) {
			promotionIdsStrings.push(promotionIds.join(','));
		} else {
			let current = '';
			promotionIds.forEach((id, index) => {
				if (current.length + id.length + 1 <= 256) {
					current += id + ',';
				} else {
					promotionIdsStrings.push(current);
					current = id;

					if (index + 1 === promotionIds.length) {
						promotionIdsStrings.push(current);
					}
				}
			});
		}

		await Promise.all(
			promotionIdsStrings.map(async (parametersString) => {
				promotions = await this.managerRepository.getCommerceApi().shopperPromotions.getPromotions({
					parameters: {
						ids: parametersString
					}
				});
			})
		);

		this.loadingPromotionIds = this.loadingPromotionIds.filter((el) => !promotionIds.includes(el));

		if (promotions.data) {
			this.storePromotions(promotions.data);
		}
	};

	/**
	 * Gets promotion that are already fetched and stored in redux
	 * @returns Promotions
	 */
	private getStoredPromotion(promotionId) {
		const store = this.managerRepository.getStoreMgr().getStore();

		return selectPromotion(store.getState(), promotionId);
	}

	/**
	 * Adds promotions to redux store
	 * @param promotionData Array of promotions
	 */
	private storePromotions(promotionData) {
		const store = this.managerRepository.getStoreMgr().getStore();
		let promotions = {};

		if (promotionData) {
			promotionData.forEach((promotion) => {
				promotions[promotion.id] = {
					...promotion
				};
			});

			store.dispatch(receivePromotions(promotions));
		}
	}
}

export default PromotionsMgr;
